// Shared helpers for surfacing exhibition metaobjects in search. Shopify's native
// search index excludes metaobjects, so exhibitions are matched client-side against
// the JSON index rendered by snippets/exhibitions-search-index.liquid.
const ExhibitionSearch = {
  _data: null,
  _label: 'Exhibitions',
  normalize(str) {
    return (str || '')
      .toString()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '') // strip diacritics so "trien lam" matches "Triển lãm"
      .replace(/[đĐ]/g, 'd')
      .toLowerCase()
      .trim();
  },
  data() {
    if (this._data !== null) return this._data;
    const el = document.getElementById('ExhibitionsSearchIndex');
    if (!el) {
      this._data = [];
      return this._data;
    }
    try {
      this._data = JSON.parse(el.textContent) || [];
    } catch (e) {
      console.error('ExhibitionsSearchIndex parse error:', e);
      this._data = [];
    }
    this._label = el.dataset.label || this._label;
    return this._data;
  },
  match(query, limit) {
    const q = this.normalize(query);
    if (!q) return [];
    const out = [];
    const items = this.data();
    for (let i = 0; i < items.length && out.length < limit; i++) {
      if (this.normalize(items[i].name).indexOf(q) !== -1) out.push(items[i]);
    }
    return out;
  },
  escape(str) {
    const div = document.createElement('div');
    div.textContent = str == null ? '' : str;
    return div.innerHTML;
  },
};

class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    if (this.resetButton) {
      this.resetButton.addEventListener('click', this.clear.bind(this));
    }
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('keydown', this.onKeydown.bind(this));
    this.activeDescendantIndex = -1;

    const isTemplateSearch = this.closest('.template-search');
    if (isTemplateSearch) {
      document.addEventListener('click', this.handleClickOutside.bind(this));
    }
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }
  get loadingButton() {
    return this.querySelector('.search__reset');
  }

  onFocus(event) {
    if (this.closest('.template-search')) {
      document.body.classList.add('predictive-search-open');
      if (this.getQuery().length === 0) {
        return;
      }
      const url = this.setupURL().toString();
      this.renderSection(url, event);
    }
  }
  getQuery() {
    return this.input.value.trim();
  }
  clear(event = null) {
    event && event.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.setExpanded(false);
  }

  get resultsContainer() {
    return document.getElementById('PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this));
  }

  get options() {
    const container = this.resultsContainer;
    return container ? Array.from(container.querySelectorAll('[role="option"]')) : [];
  }

  setExpanded(expanded) {
    if (this.input) this.input.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    if (!expanded) this.clearActiveDescendant();
  }

  clearActiveDescendant() {
    this.activeDescendantIndex = -1;
    if (this.input) this.input.removeAttribute('aria-activedescendant');
    this.options.forEach((option) => option.setAttribute('aria-selected', 'false'));
  }

  setActiveDescendant(index) {
    const options = this.options;
    if (!options.length) return;
    if (index < 0) index = options.length - 1;
    if (index >= options.length) index = 0;
    options.forEach((option, i) => option.setAttribute('aria-selected', i === index ? 'true' : 'false'));
    const current = options[index];
    if (!current.id) current.id = 'predictive-search-option-' + index;
    this.input.setAttribute('aria-activedescendant', current.id);
    current.scrollIntoView({ block: 'nearest' });
    this.activeDescendantIndex = index;
  }

  onKeydown(event) {
    if (this.getAttribute('results') !== 'true') return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.setActiveDescendant(this.activeDescendantIndex + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.setActiveDescendant(this.activeDescendantIndex - 1);
        break;
      case 'Enter': {
        const current = this.options[this.activeDescendantIndex];
        const link = current && current.querySelector('a');
        if (link) {
          event.preventDefault();
          link.click();
        }
        break;
      }
      case 'Escape':
        this.clear();
        break;
    }
  }

  announceResults(container) {
    const region = this.querySelector('[data-predictive-live]');
    if (!region) return;
    const count = container.querySelectorAll('[role="option"]').length;
    const tpl = region.dataset.resultsTpl || '[count]';
    region.textContent = tpl.replace('[count]', count);
  }

  setupURL() {
    const url = new URL(`${FoxTheme.routes.shop_url}${FoxTheme.routes.predictive_search_url}`);
    const formData = new FormData(this);

    formData.forEach((value, key) => {
      url.searchParams.set(key, value);
    });

    url.searchParams.set('options[prefix]', 'last');

    if (this.dataset.predictiveTypes) {
      url.searchParams.set('resources[type]', this.dataset.predictiveTypes);
    }

    return (
      url.searchParams.set('q', this.getQuery()),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    const shouldDisplayResults = this.renderSearchResults(responseText);
    this.toggleResults(shouldDisplayResults);
  }

  renderSectionFromFetch(url) {
    let shouldDisplayResults = false;
    // Indicate that loading has started
    this.setAttribute('loading', 'true');
    if (this.loadingButton) {
      this.loadingButton.classList.add('btn--loading');
    }

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.text();
      })
      .then((responseText) => {
        // Process and cache the fetched data
        shouldDisplayResults = this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        // Remove the loading attribute in both success and error cases
        this.removeAttribute('loading');
        if (this.loadingButton) {
          this.loadingButton.classList.remove('btn--loading');
        }
        this.toggleResults(shouldDisplayResults);
      });
  }
  toggleResults(shouldDisplayResults) {
    if (shouldDisplayResults) {
      this.setAttribute('results', 'true');
      this.setExpanded(true);
      return;
    }
    this.removeAttribute('results');
    this.setExpanded(false);
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        targetElement.innerHTML = contentElement.innerHTML;
        const exhibitionCount = this.injectExhibitions(targetElement);
        this.clearActiveDescendant();
        this.announceResults(targetElement);
        if (this.dataset.hideEmptyResults === 'true') {
          return exhibitionCount > 0 || !targetElement.querySelector('[data-predictive-empty]');
        }
        return true;
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
    return false;
  }

  // Inject matching exhibition metaobjects into the freshly-rendered predictive
  // results. Native search can't return them, so they're matched client-side.
  injectExhibitions(targetElement) {
    const limit = Number(this.dataset.resultsLimit) || 3;
    const matches = ExhibitionSearch.match(this.getQuery(), limit);
    if (!matches.length) return 0;

    // Drop the "no results" placeholders now that we have something to show.
    const emptyDrawer = targetElement.querySelector('[data-predictive-empty]');
    if (emptyDrawer) emptyDrawer.remove();
    const emptyState = targetElement.querySelector('.empty-state');
    if (emptyState) emptyState.remove();

    // Reuse the native results wrapper, or create one for the empty case.
    let wrapper = targetElement.querySelector('.grid.gap-6.w-full');
    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.className = 'grid gap-6 w-full';
      targetElement.insertBefore(wrapper, targetElement.firstChild);
    }

    const items = matches
      .map((ex) => {
        const meta = ex.dates ? '<span class="text-subtext text-sm block">' + ExhibitionSearch.escape(ex.dates) + '</span>' : '';
        const thumb = ex.image
          ? '<a href="' +
            ExhibitionSearch.escape(ex.url) +
            '" class="media-wrapper blocks-radius-sm"><img src="' +
            ExhibitionSearch.escape(ex.image) +
            '" alt="" width="100" loading="lazy" is="image-lazy"></a>'
          : '';
        return (
          '<li class="predictive-search__list-item" role="option" aria-selected="false">' +
          '<div class="flex items-center gap-3">' +
          thumb +
          '<div class="grid gap-1"><p class="block m-0"><a href="' +
          ExhibitionSearch.escape(ex.url) +
          '" class="reversed-link text-pcard-title">' +
          ExhibitionSearch.escape(ex.name) +
          '</a></p>' +
          meta +
          '</div></div></li>'
        );
      })
      .join('');

    const group = document.createElement('div');
    group.className = 'grid gap-3 predictive-search-result search__box-item predictive-search-result--exhibitions';
    group.innerHTML =
      '<h4 class="h5 predictive-search-result__heading">' +
      ExhibitionSearch.escape(ExhibitionSearch._label) +
      '</h4><div class="grid"><ul class="grid gap-4" role="group">' +
      items +
      '</ul></div>';

    // Place exhibitions ahead of the "search for …" submit footer if present.
    const footer = wrapper.querySelector('.search__results-all');
    if (footer) wrapper.insertBefore(group, footer);
    else wrapper.appendChild(group);

    return matches.length;
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      setTimeout(() => {
        document.body.classList.remove('predictive-search-open');
      });
    }
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class SearchDrawer extends DrawerComponent {
  constructor() {
    super();
  }
  get requiresBodyAppended() {
    return false;
  }
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}
customElements.define('search-drawer', SearchDrawer);
